import { useState } from 'react';
import { Modal, Box, Button, TextField } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileScreen, faSimCard, faKey, faChevronRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useTranslation } from 'react-i18next';
import { formInputSx } from '../styles/inputStyles';
import './WhatsAppSetupModal.css';
import './SocialConnectModal.css';

// Meta's Embedded Signup has exactly two onboarding variants, selected per
// FB.login call via the `featureType` extra (App Dashboard > WhatsApp >
// Embedded Signup Builder > Feature Type):
//
//   ''                                -> classic Cloud API onboarding. The
//                                        number is registered fresh on the
//                                        Cloud API, so it must NOT already be
//                                        in use by any WhatsApp account.
//   'whatsapp_business_app_onboarding' -> Coexistence. The number stays on the
//                                        customer's WhatsApp Business app and
//                                        messages are mirrored to the Cloud API.
//
// A phone number can only be registered in one place, which is why the customer
// has to tell us which situation they are in before the wizard opens. Asking up
// front is also kinder than letting Meta's wizard surprise them with "your
// number is already in use".
//
// Third path, no wizard: a business that already operates its own Cloud API
// number pastes phone_number_id + a (system user) access token; the backend
// validates them against Meta and runs the same activation. Embedded Signup
// never lists WABAs created via the developer app, so this is also the only
// way to connect Meta's App-Dashboard test number.
export const WA_FEATURE_TYPE_COEXISTENCE = 'whatsapp_business_app_onboarding';
export const WA_FEATURE_TYPE_CLASSIC = '';

const WhatsAppSetupModal = ({ open, onClose, onSelect, onManualConnect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [mode, setMode] = useState('choose');            // 'choose' | 'manual'
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setMode('choose');
    setPhoneNumberId('');
    setAccessToken('');
    setWabaId('');
    setSubmitting(false);
  };
  const handleClose = () => { reset(); onClose(); };

  const options = [
    {
      key: 'coexistence',
      icon: faMobileScreen,
      title: t('wa_setup_existing_title'),
      desc: t('wa_setup_existing_desc'),
      onClick: () => { reset(); onSelect(WA_FEATURE_TYPE_COEXISTENCE); },
    },
    {
      key: 'classic',
      icon: faSimCard,
      title: t('wa_setup_new_title'),
      desc: t('wa_setup_new_desc'),
      onClick: () => { reset(); onSelect(WA_FEATURE_TYPE_CLASSIC); },
    },
    {
      key: 'manual',
      icon: faKey,
      title: t('wa_setup_manual_title'),
      desc: t('wa_setup_manual_desc'),
      onClick: () => setMode('manual'),
    },
  ];

  const submitManual = async () => {
    if (!phoneNumberId.trim() || !accessToken.trim()) return;
    setSubmitting(true);
    try {
      await onManualConnect({ phone_number_id: phoneNumberId.trim(), access_token: accessToken.trim(), waba_id: wabaId.trim() || undefined });
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="social-connect-modal"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          width: '90%',
          maxWidth: 550,
          outline: 'none',
        }}
      >
        <Box className="social-connect-modal-header" sx={{ px: isMobile ? 2.5 : 4, py: 2, borderBottom: '1px solid #f0f0f0' }}>
        <div className="wa-setup-modal-header" style={{ marginBottom: 0 }}>
          <FontAwesomeIcon icon={faWhatsapp} style={{ color: '#25D366', fontSize: '2.5rem' }} />
          <h3 className="wa-setup-modal-title">{t('wa_setup_title')}</h3>
        </div>
        </Box>

        <Box className="social-connect-modal-body" sx={{ p: isMobile ? 2.5 : 4 }}>

        {mode === 'choose' && (
          <>
            <p className="wa-setup-modal-intro">{t('wa_setup_intro')}</p>
            <div className="wa-setup-options">
              {options.map((option) => (
                <button key={option.key} type="button" className="wa-setup-option" onClick={option.onClick}>
                  <FontAwesomeIcon icon={option.icon} className="wa-setup-option-icon" />
                  <span className="wa-setup-option-text">
                    <span className="wa-setup-option-title">{option.title}</span>
                    <span className="wa-setup-option-desc">{option.desc}</span>
                  </span>
                  <FontAwesomeIcon icon={faChevronRight} className="wa-setup-option-arrow" />
                </button>
              ))}
            </div>
          </>
        )}

        {mode === 'manual' && (
          <>
            <p className="wa-setup-modal-intro">{t('wa_setup_manual_intro')}</p>
            <div className="wa-setup-manual-form">
              <TextField
                label={t('wa_setup_manual_phone_id')}
                value={phoneNumberId}
                onChange={(e) => setPhoneNumberId(e.target.value)}
                fullWidth
                size="small"
                sx={formInputSx}
                inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
              />
              <TextField
                label={t('wa_setup_manual_token')}
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                fullWidth
                size="small"
                type="password"
                sx={formInputSx}
                inputProps={{ autoComplete: 'off' }}
              />
              <TextField
                label={t('wa_setup_manual_waba_id')}
                value={wabaId}
                onChange={(e) => setWabaId(e.target.value)}
                fullWidth
                size="small"
                sx={formInputSx}
                inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
              />
            </div>
          </>
        )}
        </Box>

        {mode === 'manual' && (
            <div className="wa-setup-modal-actions wa-setup-modal-actions-row social-connect-modal-footer">
              <Button
                onClick={() => setMode('choose')}
                variant="outlined"
                disabled={submitting}
                sx={{ color: '#5616ea', borderColor: '#5616ea', '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' } }}
              >
                {t('back')}
              </Button>
              <Button
                onClick={submitManual}
                variant="contained"
                disabled={submitting || !phoneNumberId.trim() || !accessToken.trim()}
                sx={{ backgroundColor: '#5616ea', '&:hover': { backgroundColor: '#4a14d6' } }}
              >
                {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : t('wa_setup_manual_submit')}
              </Button>
            </div>
        )}

        {mode === 'choose' && (
          <div className="wa-setup-modal-actions social-connect-modal-footer">
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                color: '#5616ea',
                borderColor: '#5616ea',
                '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                width: '100%',
              }}
            >
              {t('cancel')}
            </Button>
          </div>
        )}
      </Box>
    </Modal>
  );
};

export default WhatsAppSetupModal;
